import { yupResolver } from "@hookform/resolvers/yup";
import { Check, Copy, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";

const schema = yup.object().shape({
  integrationEmail: yup.string().email("Invalid email").nonNullable(),
});

type IApiIntegrationSectionFormValues = yup.InferType<typeof schema>;

export const ApiIntegrationSection = () => {
  const [copied, setCopied] = useState<boolean>(false);

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  const onCopy = useCallback(async (): Promise<void> => {
    if (!lenderCompany) return;

    await navigator.clipboard.writeText(lenderCompany.clientId);
    setCopied(true);
    toast.success("Client ID copied to clipboard");
    window.setTimeout(() => setCopied(false), 1_500);
  }, [lenderCompany]);

  const form = useForm<IApiIntegrationSectionFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      integrationEmail: "",
    },
  });

  useEffect(() => {
    if (lenderCompany) {
      const initialValues: IApiIntegrationSectionFormValues = {};

      if (lenderCompany.integrationEmail) {
        initialValues.integrationEmail = lenderCompany.integrationEmail;
      }
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: () => {
            toast.success("API integration updated successfully");
          },
          onError: () => {
            toast.error("Failed to update API integration");
          },
        },
      );
    })();
  }, [form, updateLenderCompany, lenderCompany]);

  return (
    <Card>
      <Form {...form}>
        <CardContent className="space-y-6">
          <div>
            <h1 className="text-xl font-bold">API Integration</h1>
            <p className="text-sm text-black-100">
              Configure the API integration to receive notifications about
              events in your account.
            </p>
          </div>
          <Separator />
          <Alert
            variant="plain"
            className="border-yellow-200 bg-yellow-50"
            leftElement={
              <TriangleAlert className="mr-4 size-8 fill-yellow-600 stroke-white" />
            }
          >
            <AlertTitle className="text-yellow-900">
              Keep your credentials secure
            </AlertTitle>
            <AlertDescription className="text-yellow-700">
              Never share your Client ID or API keys publicly. Store them
              securely in your environment variables. If you suspect a security
              breach, please contact us immediately.
            </AlertDescription>
          </Alert>

          <div>
            <label
              htmlFor="client-id"
              className="mb-2 block text-sm font-medium"
            >
              Client ID
            </label>

            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input value={lenderCompany?.clientId} readOnly />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onCopy}
                aria-label="Copy Client ID"
                className="text-primary outline-none ring-0 focus-visible:ring-[none]"
              >
                {copied ? (
                  <Check size={16} strokeWidth={3} />
                ) : (
                  <Copy size={16} strokeWidth={3} />
                )}
              </Button>
            </div>

            <p className="mt-2 text-xs text-black-100">
              Use this unique identifier to authenticate API requests.
            </p>
          </div>

          <FormField
            control={form.control}
            name="integrationEmail"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormLabel>Integration Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="integrations@your-company.com"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>
                  Email address for integration-related notifications and
                  alerts.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Separator />
          <div className="flex justify-end">
            <Button
              type="button"
              size="lg"
              onClick={handleSubmit}
              disabled={
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                !form.formState.isDirty
              }
            >
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Form>
    </Card>
  );
};
